import React from "react";
import PropTypes from "prop-types";
import Card from "../Card";
import ButtonGridLayout from "../ButtonGridLayout";
import SectionTitle from "../SectionTitle";
import Loader from "../Loader";
import Colors from "../../global/color";

import {
  StyledGridLayout,
  StyledGridColumn,
  StyledInfiniteScroll,
  StyledEndMessage,
  StyledInfiniteScrollTablet,
  StyledInfiniteScrollMobile,
} from "./styles";

export const GridLayout = ({
  index = 0,
  context = null,
  clientContext = null,
  key = "grid-card",
  id = "grid-card",
  data = null,
  schema = null,
  sectionLabel = null,
  gridTitle = null,
  gridDescription = null,
  showButton = false,
  buttonLabel = null,
  buttonPath = null,
  gridBackgroundImage = null,
  layoutClass = null,
  layoutPosition = null,
  loadMoreContent = null,
  total = null,
  hasMore = false,
  calculateHasMore = null,
  className = null,
  currentCategorySettings = null,
  skip = null, // Note: not used, but might use depending on how we can connect startSlice value to pass slice value from Sections > Section
  setHasMore = false,
  startSlice = 5, // Value of the start slice - 1 to figure out where the end of the previous component was &
  loadMoreData = null,
  dataPath = null,
  section = null,
}) => {
  let { isMobile, isTablet } = clientContext;
  let { settings } = context.data;
  let { loadMoreContentComplete } = settings.strings;
  const isClient = typeof window === "object";

  if (data && data.length > 0) {
    if (isClient === true) {
        data = window.api.context.data.lists.items.slice(
          startSlice,
          window.api.context.data.lists.items.length
        );
        if (calculateHasMore !== null) {
          hasMore = calculateHasMore(
            data,
            window.api.context.data.lists.total - startSlice,
            startSlice
          );
        }
    }

    // If is a queue content type, use the data entries instead of the contentful items.
    let contentType = data[0].sys.contentType.sys.id;
    let contentItems = data;

    // NOTE: the infinite scroll data may not be compatible with queue data that's loaded. Also we aren't planning to have long queues of data.

    if (contentType === "queue") {
      contentItems = data[0].fields.entries;
    }

    let getImageDimensions = ({ clientContext }) => {
      let { isMobile, isTablet } = clientContext;
      if (isMobile === true && isTablet === false) {
        return {
          large: {
            width: 147,
            height: 147,
          },
        };
      } else if (isMobile === false && isTablet === true) {
        return {
          large: {
            width: 142,
            height: 142,
          },
        };
      } else {
        return {
          large: {
            width: 182,
            height: 182,
          },
        };
      }
    };

    let imageDimensions = getImageDimensions({ clientContext });

    let getRows = () => {
      let GridRowStyle = StyledInfiniteScroll;
      if (isMobile === true) {
        GridRowStyle = StyledInfiniteScrollMobile;
      } else if (isTablet === true) {
        GridRowStyle = StyledInfiniteScrollTablet;
      } else {
        GridRowStyle = StyledInfiniteScroll;
      }

      return (
        <>
          <GridRowStyle
            dataLength={contentItems.length}
            next={() => loadMoreContent()}
            hasMore={hasMore}
            loader={<Loader />}
            scrollThreshold="80%"
          >
            {contentItems.map((item, index) => {
              return (
                <StyledGridColumn
                  style={{
                    border: `1px solid ${Colors.lightGrey}`,
                  }}
                >
                  <Card
                    key={`GridLayout_630_2x3_Infinite_List-card-${index}`}
                    data={{ ...item }}
                    context={context}
                    clientContext={clientContext}
                    component="GridLayout_630_2x3_Infinite_List"
                    index={index}
                    padding={null}
                    width={imageDimensions.large.width}
                    height={imageDimensions.large.height}
                    imageLocation="left"
                    textLayout="TextLayoutList400Row"
                    currentCategorySettings={currentCategorySettings}
                  dataPath={dataPath}
                  section={section}
                  />
                </StyledGridColumn>
              );
            })}
          </GridRowStyle>
          {!hasMore && contentItems.length > 6 && (
            <StyledEndMessage>{loadMoreContentComplete}</StyledEndMessage>
          )}
        </>
      );
    };

    let groupedGridRows = getRows();

    if (isMobile && !isTablet) {
      return (
        <>
          {sectionLabel && (
            <SectionTitle
              context={context}
              clientContext={clientContext}
              fullWidth={true}
              style={{ marginTop: "40px" }}
            >
              {sectionLabel}
            </SectionTitle>
          )}
          <StyledGridLayout style={{ marginTop: "10px" }}>
            {groupedGridRows}
          </StyledGridLayout>
          {hasMore && (
            <div style={{marginTop: "20px"}}>
              <ButtonGridLayout
                onClick={() => loadMoreContent(skip)}
                label={buttonLabel}
              />
            </div>
          )}
        </>
      );
    }
    else if (isTablet && !isMobile) {
      return (
        <>
          {sectionLabel && (
            <SectionTitle
              context={context}
              clientContext={clientContext}
              fullWidth={true}
              style={{ marginTop: "40px" }}
            >
              {sectionLabel}
            </SectionTitle>
          )}
          <StyledGridLayout style={{ marginTop: "20px" }}>
            {groupedGridRows}
          </StyledGridLayout>
          {hasMore && (
            <div style={{marginTop: "20px"}}>
              <ButtonGridLayout
                onClick={() => loadMoreContent(skip)}
                label={buttonLabel}
              />
            </div>
          )}
        </>
      );
    }
    else {
      return (
        <>
          {sectionLabel && (
            <SectionTitle
              context={context}
              clientContext={clientContext}
              padding={{ marginTop: "40px" }}
            >
              {sectionLabel}
            </SectionTitle>
          )}
          <StyledGridLayout style={{ marginTop: "40px" }}>
            {groupedGridRows}
          </StyledGridLayout>
          {hasMore && (
            <div style={{marginTop: "20px"}}>
              <ButtonGridLayout
                onClick={() => loadMoreContent(skip)}
                label={buttonLabel}
              />
            </div>
          )}
        </>
      );
    }
  } else {
    return null;
  }
};

export default GridLayout;

GridLayout.propTypes = {
  /**
   Client side variables
  */
  clientContext: PropTypes.object,
  /**
   Server side variables
  */

  context: PropTypes.object.isRequired,
  /**
    Key name for component instance
  */
  key: PropTypes.string,
  /**
    Unique id for this section. Must be alphanumeric with no spaces.
  */
  id: PropTypes.string,
  /**
    Data object. Should be array with items returned from content. If queue, pass it in as an array.
  */
  data: PropTypes.array,
  /**
  Which type of data to display. Options: article, list, queue, or any for mixed results.
  */
  schema: PropTypes.string,
  /**
  Label for the section title (which is a label and section separator.)
  */
  sectionLabel: PropTypes.string,
  /**
  Text of grid component's display title, if any.
  */
  gridTitle: PropTypes.string,
  /**
  If the Call to action button should show.
  */
  showButton: PropTypes.bool,
  /**
  Button Label for Call to action.
  */
  buttonLabel: PropTypes.string,
  /**
  Path for Call to action button.
  */
  buttonPath: PropTypes.string,
};
